import { createApiClient } from './axios-instance';
import { BaseResponse } from './auth.service';
import { PaginationMeta } from '../types';

// Types
export interface AppSeat {
  id: string;
  workspace_id: string;
  app_id: string;
  account_id: string;
  account?: {
    id: string;
    email: string;
    display_name: string | null;
  };
  created_at: string;
  updated_at: string;
}

export interface BulkUpdateSeatsRequest {
  account_ids: string[];
}

export interface BulkUpdateSeatsResult {
  added: number;
  removed: number;
  total: number;
}

export interface AppSeatsResponse {
  seats: AppSeat[];
  pagination: PaginationMeta;
}

// API Service
class AppSeatService {
  /**
   * Get seat assignments for an app in a workspace with pagination
   */
  async getAppSeats(
    workspaceId: string,
    appId: string,
    idToken: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<AppSeatsResponse> {
    const client = createApiClient(idToken);
    const response = await client.get<BaseResponse<AppSeat[]> & { pagination?: PaginationMeta }>(
      `/workspaces/${workspaceId}/apps/${appId}/seats`,
      {
        params: { page, page_size: pageSize },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get app seats');
    }

    return {
      seats: response.data.data || [],
      pagination: response.data.pagination || {
        page: 1,
        page_size: pageSize,
        total: 0,
        total_pages: 0,
      },
    };
  }

  /**
   * Bulk update seat assignments for an app
   * Replaces all current seat assignments with the provided account IDs
   */
  async bulkUpdateSeats(
    workspaceId: string,
    appId: string,
    accountIds: string[],
    idToken: string
  ): Promise<BulkUpdateSeatsResult> {
    const client = createApiClient(idToken);
    const response = await client.post<BaseResponse<BulkUpdateSeatsResult>>(
      `/workspaces/${workspaceId}/apps/${appId}/seats`,
      {
        account_ids: accountIds,
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update seat assignments');
    }

    return response.data.data!;
  }

  /**
   * Check if an account has a seat for an app
   */
  async checkAccountHasSeat(
    workspaceId: string,
    appId: string,
    accountId: string,
    idToken: string
  ): Promise<boolean> {
    try {
      const response = await this.getAppSeats(workspaceId, appId, idToken);
      return response.seats.some((seat: AppSeat) => seat.account_id === accountId);
    } catch (error) {
      console.error('Failed to check seat access:', error);
      return false;
    }
  }
}

export const appSeatService = new AppSeatService();
